import { MeQuery } from '@graphql/queries/__generated__/me.generated'
import CloseIcon from '@mui/icons-material/Close'
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import { Theme } from '@mui/material'
import { orange } from '@mui/material/colors'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import makeStyles from '@mui/styles/makeStyles'
import React, { useMemo, useRef } from 'react'
import { Link } from 'react-router-dom'
import AccountIcon from '../../../../icons/AccountIcon'
import AccountIconFilled from '../../../../icons/AccountIconFilled'
import BagIcon from '../../../../icons/BagIcon'
import BagIconFilled from '../../../../icons/BagIconFilled'
import FavoritesIcon from '../../../../icons/FavoritesIcon'
import FavoritesIconFilled from '../../../../icons/FavoritesIconFilled'
import StarIcon from '../../../../icons/StarIcon'
import StarIconFilled from '../../../../icons/StarIconFilled'
import { ACCOUNT_LOGIN_URL, CART_URL, PROFILE_FAVORITES_URL, PROFILE_REWARDS_URL } from '../../../urls'
import HeaderCart from './HeaderCart'
import HeaderFavorites from './HeaderFavorites'
import { WholesaleAppStatusEnum } from '../../../../generated/gqlTypes'
import { useTranslation } from 'react-i18next'
import HeaderProfilePanel from '@components/Header/components/HeaderProfilePanel'
import PersonOffIcon from '@mui/icons-material/PersonOff'
import { useHover } from 'usehooks-ts'
import Tooltip from '@mui/material/Tooltip'
import HeaderCurrencyPanel from '@components/Header/components/HeaderCurrencyPanel'
import { getCurrencySymbol } from '../../../utils/misc'
import useLocalStorage from '../../../hooks/useLocalStorage'
import { LOCAL_STORAGE_KEYS } from '../../../../constants'

const useStyles = makeStyles((theme: Theme) => ({
  item: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-around',
    alignItems: 'center',
    textDecoration: 'none',
    '& svg:last-of-type': {
      display: 'none',
    },
    '&:hover': {
      '& svg:last-of-type': {
        display: 'block',
      },
      '& svg:first-of-type': {
        display: 'none',
      },
    },
  },
  bagNotEmpty: {
    color: orange['500'],
  },
  accountMenuWrapper: {
    '&:hover $panel': {
      opacity: 1,
      visibility: 'visible',
    },
  },
  cartWrapper: {
    '&:hover $panel': {
      opacity: 1,
      visibility: 'visible',
    },
  },
  favoritesMenuWrapper: {
    '&:hover $panel': {
      opacity: 1,
      visibility: 'visible',
    },
  },
  currencyMenuWrapper: {
    '&:hover $panel': {
      opacity: 1,
      visibility: 'visible',
    },
  },
  panel: {
    padding: '24px',
    cursor: 'initial',
    width: 360,
    boxShadow: '0 6px 12px rgb(0 0 0 / 6%)',
    visibility: 'hidden',
    opacity: 0,
    position: 'absolute',
    top: '100%',
    right: 0,
    borderTop: '1px solid #eee',
    backgroundColor: '#fff',
    transition: 'all .1s ease-in-out',
    zIndex: 1,
  },
  panelCart: {
    padding: 0,
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  panelFavorites: {
    padding: 0,
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  panelCurrency: {
    padding: 0,
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
}))

interface IProps {
  user: MeQuery['me'] | null
  isStaff?: boolean
  cartCount: number
  isWholesaleMode: boolean

  handleClickBag(e: any): void

  handleClickSignIn(e: any): void

  setIsActiveOverlay(val: boolean): void

  handleClickContactUs(): void

  handleClickWSMode(): void
}

const HeaderUtilities: React.FC<IProps> = ({
  user,
  isStaff,
  cartCount,
  handleClickBag,
  handleClickSignIn,
  setIsActiveOverlay,
  handleClickContactUs,
  handleClickWSMode,
  isWholesaleMode,
}) => {
  const { t } = useTranslation()
  const classes = useStyles()
  const theme = useTheme()
  const smDown = useMediaQuery(theme.breakpoints.down('sm'))
  const hoverFavoritesRef = useRef(null)
  const isHoverFavorites = useHover(hoverFavoritesRef)
  const hoverCurrencyRef = useRef(null)
  const isHoverCurrency = useHover(hoverCurrencyRef)
  const hoverBagRef = useRef(null)
  const isHoverBag = useHover(hoverBagRef)

  const [, , getStorageCurrency] = useLocalStorage(LOCAL_STORAGE_KEYS.CURRENCY, 'USD')

  const canShowWholesaleMode =
    user?.isStaff || user?.wholesaleApp?.status === WholesaleAppStatusEnum.Confirmed
  const isImpersonate = user?.isImpersonate

  return (
    <ul className='utilities-list list-unstyled h-full items-center'>
      {user?.isStaff && (
        <li
          className={`relative utilities-list-item utilities-favorites hidden-on-affix hidden lg:flex ${classes.currencyMenuWrapper}`}
          onMouseEnter={() => (!smDown ? setIsActiveOverlay(true) : undefined)}
          onMouseLeave={() => (!smDown ? setIsActiveOverlay(false) : undefined)}
          ref={hoverCurrencyRef}
        >
          <div className={classes.item}>
            <span className='text-base cursor-pointer'>{getCurrencySymbol(getStorageCurrency())}/EN</span>
          </div>
          <div className={`${classes.panel} ${classes.panelCurrency}`}>
            <HeaderCurrencyPanel isHover={isHoverCurrency} />
          </div>
        </li>
      )}
      {isImpersonate && (
        <li
          className='utilities-list-item hidden md:flex'
          onClick={() => (window.location.href = '/impersonate/stop/')}
        >
          <div className={classes.item}>
            <PersonOffIcon />
            <PersonOffIcon />
          </div>
        </li>
      )}
      {canShowWholesaleMode && (
        <li className={`utilities-list-item utilities-search flex`} onClick={handleClickWSMode}>
          <Tooltip
            title={t('header.wsMode', 'WS mode')}
            arrow
            placement='bottom'
            componentsProps={{
              tooltip: {
                sx: { backgroundColor: '#000' },
              },
              arrow: {
                sx: { color: '#000' },
              },
            }}
          >
            <div className={classes.item}>
              {isWholesaleMode && (
                <>
                  <CloseIcon />
                  <CloseIcon />
                </>
              )}
              {!isWholesaleMode && (
                <>
                  <ReceiptLongOutlinedIcon />
                  <ReceiptLongIcon />
                </>
              )}
            </div>
          </Tooltip>
        </li>
      )}
      {!user && (
        <li className={`utilities-list-item hidden-on-affix utilities-account hidden md:flex`}>
          <Tooltip
            title={t('signIn', 'Sign In')}
            arrow
            placement='bottom'
            componentsProps={{
              tooltip: {
                sx: { backgroundColor: '#000' },
              },
              arrow: {
                sx: { color: '#000' },
              },
            }}
          >
            <Link to={ACCOUNT_LOGIN_URL} onClick={handleClickSignIn} className={classes.item}>
              <AccountIcon />
              <AccountIconFilled />
            </Link>
          </Tooltip>
        </li>
      )}

      {isStaff && (
        <li className='utilities-list-item utilities-dashboard relative header-dashboard dashboard-vs hidden sm:flex'>
          <Tooltip
            title={t('header.dashboard', 'Dashboard')}
            arrow
            placement='bottom'
            componentsProps={{
              tooltip: {
                sx: { backgroundColor: '#000' },
              },
              arrow: {
                sx: { color: '#000' },
              },
            }}
          >
            <a aria-label='Dashboard' className={`dashboard-button analytics-data ${classes.item}`} href='/dashboard/'>
              <StarIcon viewBox='0 0 487.222 487.222' />
              <StarIconFilled viewBox='0 0 19.481 19.481' />
            </a>
          </Tooltip>
        </li>
      )}
      {user && (
        <li
          className={`relative utilities-list-item utilities-favorites hidden-on-affix hidden sm:flex ${classes.favoritesMenuWrapper}`}
          onMouseEnter={() => (!smDown ? setIsActiveOverlay(true) : undefined)}
          onMouseLeave={() => (!smDown ? setIsActiveOverlay(false) : undefined)}
          ref={hoverFavoritesRef}
        >
          <Link
            to={PROFILE_FAVORITES_URL}
            aria-label='Favorites'
            className={`favorites-button analytics-data ${classes.item}`}
            data-analytics_interaction_name='favorites'
            data-analytics_interaction_desc='favorites_header'
            data-analytics_event='click'
          >
            <FavoritesIcon />
            <FavoritesIconFilled />
          </Link>
          <div className={`${classes.panel} ${classes.panelFavorites}`}>
            <HeaderFavorites isHover={isHoverFavorites} />
          </div>
        </li>
      )}
      {user && (
        <li
          className={`relative utilities-list-item hidden-on-affix utilities-account hidden md:flex ${classes.accountMenuWrapper}`}
          onMouseEnter={() => setIsActiveOverlay(true)}
          onMouseLeave={() => setIsActiveOverlay(false)}
        >
          <Link to={PROFILE_REWARDS_URL} onClick={user ? undefined : handleClickSignIn} className={classes.item}>
            <AccountIcon />
            <AccountIconFilled />
          </Link>
          <div className={classes.panel}>
            <HeaderProfilePanel user={user} handleClickContactUs={handleClickContactUs} />
          </div>
        </li>
      )}
      <li
        className={`utilities-list-item utilities-cart relative flex ${classes.cartWrapper}`}
        onMouseEnter={() => (!smDown ? setIsActiveOverlay(true) : undefined)}
        onMouseLeave={() => (!smDown ? setIsActiveOverlay(false) : undefined)}
        ref={hoverBagRef}
      >
        <Link
          to={CART_URL}
          onClick={!cartCount ? handleClickBag : null}
          aria-label='Shopping Bag'
          className={`relative bag-button ${classes.item}`}
        >
          {cartCount > 0 && (
            <>
              <BagIconFilled viewBox='0 0 21 26' className={classes.bagNotEmpty} />
              <BagIconFilled viewBox='0 0 21 26' className={classes.bagNotEmpty} />
              <span className='bag-indicator'>
                <span className='bag-indicator-content text-white'>{cartCount}</span>
              </span>
            </>
          )}
          {!cartCount && (
            <>
              <BagIcon viewBox='0 0 21 26' />
              <BagIconFilled viewBox='0 0 21 26' />
              <span className='bag-indicator'>
                <span className='bag-indicator-content'>{cartCount}</span>
              </span>
            </>
          )}
        </Link>
        <div className={`${classes.panel} ${classes.panelCart}`}>
          <HeaderCart isHover={isHoverBag} />
        </div>
      </li>
    </ul>
  )
}

export default HeaderUtilities
